import {
  type RowData,
  type SortFn,
  createColumnHelper,
  createSortedRowModel,
  metaHelper,
  rowSortingFeature,
  sortFn_basic,
  sortFn_datetime,
  tableFeatures,
} from '@tanstack/react-table';
import { compareDecimal } from '@/lib/money';

export interface DataTableColumnMeta {
  /** Número e dinheiro alinham à direita, para comparar coluna abaixo. */
  align?: 'right';
  /**
   * Coluna secundária some abaixo desta largura (§20: tabela extensa precisa de estratégia no
   * celular). A primeira coluna e a de ações nunca devem sumir.
   */
  hideBelow?: 'sm' | 'md' | 'lg';
  className?: string;
}

const collator = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

/** Texto em ordem de dicionário: "Área" junto de "area", "Item 2" antes de "Item 10". */
// `any` nos genéricos: a mesma função serve a qualquer tabela; o TanStack a tipa assim.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sortFn_ptText: SortFn<any, any> = (a, b, columnId) =>
  collator.compare(String(a.getValue(columnId) ?? ''), String(b.getValue(columnId) ?? ''));

/**
 * Dinheiro e decimais que chegam como texto ("89.90"): ordem numérica sem virar `number`. Coluna
 * de dinheiro devolve `undefined` no acessor quando não há valor, para ir ao fim da lista.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sortFn_decimal: SortFn<any, any> = (a, b, columnId) =>
  compareDecimal(String(a.getValue(columnId)), String(b.getValue(columnId)));

/**
 * Recursos de toda tabela do troig, registrados uma vez (v9 exige registro explícito). Novo
 * recurso — seleção, paginação — entra aqui quando a primeira tela precisar, não antes.
 */
export const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: {
    text: sortFn_ptText,
    decimal: sortFn_decimal,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
  },
  columnMeta: metaHelper<DataTableColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;

/** Ajudante de colunas já ligado aos recursos do troig: `const col = columnsFor<Produto>()`. */
export function columnsFor<T extends RowData>() {
  return createColumnHelper<DataTableFeatures, T>();
}
